import { effect, inject, Injectable, Signal, signal } from '@angular/core';
import { WheelAction, WheelBonus, WheelButtonConfig, WheelDynamicText, WheelPrize } from '../models/wheel-of-fortune.model';
import { adaptBonusForDisplay, wheelButtons, wheelDescriptions, wheelHeaders, wheelSubtitles } from '../data/wheel-of-fortune.config';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, delay, filter, map, startWith, tap } from 'rxjs';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { WheelOfFortuneApiService } from './wheel-of-fortune-api.service';
import { StorageLogicService } from '../../utils/services/storage-logic.service';
import { FortuneWheelCityConfig, FortuneWheelGlobalConfig } from '../../cityDynamicSettings/city-dynamic-settings.model';
import { ProfileDataservice } from 'src/app/pages/profile/services/profile-data.service';
import { toSignal } from '@angular/core/rxjs-interop';


@Injectable({
  providedIn: 'root'
})
export class WheelOfFortuneService {
  private router = inject(Router);

  private apiService = inject(WheelOfFortuneApiService);

  private readonly storageService = inject(StorageLogicService);
  
  private readonly profileData = inject(ProfileDataservice);

  private wheelTrophy = signal<WheelPrize|null>(null);

  private _wheelModalRef: BehaviorSubject<NgbModalRef | null> = new BehaviorSubject<NgbModalRef | null>(null);

  private _showSpinMarket = signal(false);

  spinsLeft = signal<number>(0);

  isUserAuthorized = toSignal(this.profileData.authDataStream.pipe(map(data => Boolean(data))));

  wheel_settings!: {
    global: FortuneWheelGlobalConfig,
    city: FortuneWheelCityConfig
  }

  wheelHeader = signal<WheelDynamicText | null>(wheelHeaders.default());

  wheelDescription = signal<WheelDynamicText | null>(null);
  
  wheelButtons = signal<WheelButtonConfig[]>([]);

  wheelSubtitle = signal<WheelDynamicText | null>(null);

  wheelBonuses = signal<WheelBonus[]>([]);

  private isProfileRouteWithoutModal = signal<boolean>(false);

  constructor() {
    this.isProfileWithoutWheelModal();

    effect(()=>{
      /** Handle logged out user  */
      if(!this.isUserAuthorized() && this.storageService.getSessionData('wheel_settings')){
        this.initWheelSettings();
        this.setDefaultUnauthorized();
      }
    }, {allowSignalWrites: true})
  }

  get modalRef() {
    return this._wheelModalRef;
  }
  private isProfileWithoutWheelModal(): void {
    combineLatest([
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd),
          map(e => e as NavigationEnd)),
      this._wheelModalRef.pipe(startWith(null))
    ])
      .pipe(
        tap(([navEvent, modalRef]) => this.isProfileRouteWithoutModal.set(navEvent.url.includes('profile') && !modalRef))
      )
      .subscribe();
  }

  private initWheelSettings() {
    this.wheel_settings = this.storageService.getSessionData('wheel_settings') as {
      global: FortuneWheelGlobalConfig,
      city: FortuneWheelCityConfig
    };
  }

  public setDefaultView(spins: number, bonuses: number) {
    this.initWheelSettings();

    if (this.isUserAuthorized()) {
      this.setDefaultAuthorized(spins, bonuses);
    }else{
      this.setDefaultUnauthorized();
    }
  }

  private setDefaultAuthorized(spins: number, bonuses: number) {
    if (this.isProfileRouteWithoutModal()) {
      return this.setDefaultProfileConfig(spins, bonuses);
    }
    this.wheelHeader.set(wheelHeaders.default());
    this.wheelDescription.set(this.defaultDescription);
    this.wheelSubtitle.set(wheelSubtitles.default(spins));

    if (spins) {
      this.wheelButtons.set([wheelButtons.spin()])
    } else {
      this.wheelButtons.set([wheelButtons.orderForSpins(this.wheel_settings.global.order_spins), wheelButtons.exchangeBonusesForSpins()])
    }
  }


  private get defaultDescription() {
    return wheelDescriptions.default(
      this.wheel_settings.city.min_total_cart_to_get_spins, 
      this.wheel_settings.global.order_spins,
      this.wheel_settings.city.product_prize_expiry_days,
      this.wheel_settings.global.registration_spins_expiry_days,
      this.wheel_settings.global.registration_spins
    )
  }

  private setDefaultProfileConfig(spins: number, bonuses: number) {
    this.wheelHeader.set(spins ? wheelHeaders.default() : wheelHeaders.outOfSpins());
    this.wheelDescription.set(spins ? this.defaultDescription : wheelDescriptions.profile_out_of_spins(this.wheel_settings.city.min_total_cart_to_get_spins, this.wheel_settings.global.order_spins, this.wheel_settings.city.product_prize_expiry_days, this.wheel_settings.global.registration_spins_expiry_days, this.wheel_settings.global.registration_spins));
    this.wheelSubtitle.set(wheelSubtitles.default(spins));

    if (spins) {
      this.wheelButtons.set([wheelButtons.spin()])
    } else {
      this.wheelButtons.set([wheelButtons.profile_order(this.wheel_settings.global.order_spins), wheelButtons.exchangeBonusesForSpins()])
    }
  }


  private setDefaultUnauthorized() {
    this.wheelHeader.set(wheelHeaders.default());
    this.wheelDescription.set(this.defaultDescription);
    this.wheelButtons.set([wheelButtons.authorize()]);
    this.wheelSubtitle.set(null);
  }

  public setAfterSpinView() {
    this.wheelButtons.set([
      wheelButtons.order(),
      wheelButtons.returnToWheel()
    ])

    this.wheelSubtitle.set(null);
  }

  public setMarketView(bonuses = 0) {
    this.wheelHeader.set(wheelHeaders.spinMarket(bonuses));
    this.wheelDescription.set(null);
    this.wheelSubtitle.set(null);
    this.wheelButtons.set([wheelButtons.returnToWheel()]);

    this.setBonusMarketOptions();
  }

  private setBonusMarketOptions() {
    const bonusMarket = this.storageService.getSessionData('wheel_settings')?.bonus_market ?? [];
    this.wheelBonuses.set(bonusMarket);
  }


  get wheelSettings(): {global:FortuneWheelGlobalConfig, city:FortuneWheelCityConfig} {
    return this.storageService.getData('wheel_settings');
  }

  get currentWheelTrophy(): Signal<WheelPrize|null>{
    return this.wheelTrophy.asReadonly();
  }

  get showSpinMarket() {
   return this._showSpinMarket.asReadonly();
  }

  get shouldShowProfileSpecificWheelContent() {
    return this.isProfileRouteWithoutModal.asReadonly();
  }


  public updateWheelModalRef(ref: NgbModalRef | null) {
    this._wheelModalRef.next(ref);
  }

  public handleWheelAction(action:WheelAction): void {
    switch (action) {
      case WheelAction.AUTHORIZE:
        break;
      case WheelAction.BUY_SPINS:
        this._showSpinMarket.set(true);
        this.wheelTrophy.set(null);
        break;
      case WheelAction.ORDER:
      case WheelAction.NAVIGATE_TO_HOME:
        this._wheelModalRef?.value?.close();
        this.router.navigate(['/home']);
        break;
      case WheelAction.NAVIGATE_TO_WHEEL:
        this.setWheelTrophy(null);
        this._showSpinMarket.set(false);
        break;
      case WheelAction.SPIN:
        break;
      default:
        break;
    }
  }

  public setWheelTrophy(trophy:WheelPrize|null): void {
    this.wheelTrophy.set(trophy);
  }

  public requestWheelSpinResult() {
    return this.apiService.getSpinResult()
      .pipe(
        delay(3000),
        filter(resp => resp.success),
        tap(({ data }) => {
          if (data) {
            this.setWheelTrophy(adaptBonusForDisplay(data));
          }
        }),
        map(({ data }) => ({ data })),
      );
  }
}
